// Singleton WebSocket client for HEXA Team Chat.
// Listens for: message, edit, delete, reaction, room_updated, typing, presence.
// Auto-reconnects with exponential backoff.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = (() => {
  if (!BACKEND_URL) return "";
  const u = new URL(BACKEND_URL);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  return `${u.origin}/api/ws/chat`;
})();

class ChatBus {
  constructor() {
    this.ws = null;
    this.token = null;
    this.listeners = new Set();
    this.online = new Set();
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.intentionalClose = false;
  }

  connect(token) {
    if (!token || !WS_URL) return;
    if (this.ws && this.ws.readyState <= 1 && this.token === token) return;
    this.disconnect(/*silent*/ true);
    this.token = token;
    this.intentionalClose = false;
    try {
      const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
      this.ws = ws;
      ws.onopen = () => {
        this.reconnectAttempts = 0;
        this._emit({ type: "_socket_open" });
      };
      ws.onmessage = (ev) => {
        let data;
        try { data = JSON.parse(ev.data); } catch { return; }
        if (data.type === "presence" && Array.isArray(data.online)) {
          this.online = new Set(data.online);
        }
        this._emit(data);
      };
      ws.onclose = () => {
        this._emit({ type: "_socket_close" });
        if (!this.intentionalClose) this._scheduleReconnect();
      };
      ws.onerror = () => { /* swallow — onclose handles reconnect */ };
    } catch {
      this._scheduleReconnect();
    }
  }

  _scheduleReconnect() {
    if (this.reconnectTimer || !this.token) return;
    this.reconnectAttempts += 1;
    const delay = Math.min(30000, 1000 * Math.pow(2, this.reconnectAttempts - 1));
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(this.token);
    }, delay);
  }

  disconnect(silent = false) {
    this.intentionalClose = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.ws) {
      try { this.ws.close(); } catch { /* noop */ }
      this.ws = null;
    }
    if (!silent) this.online = new Set();
  }

  send(payload) {
    if (this.ws && this.ws.readyState === 1) {
      try { this.ws.send(JSON.stringify(payload)); } catch { /* noop */ }
    }
  }

  sendTyping(room_id) {
    this.send({ type: "typing", room_id });
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  _emit(evt) {
    for (const l of Array.from(this.listeners)) {
      try { l(evt); } catch { /* noop */ }
    }
  }

  isOnline(address) {
    return this.online.has(address);
  }
}

export const chatBus = new ChatBus();
